import { Component } from '@theme/component';
import {
  center,
  closest,
  clamp,
  mediaQueryLarge,
  prefersReducedMotion,
  preventDefault,
  viewTransition,
  scheduler,
} from '@theme/utilities';
import { Scroller, scrollIntoView } from '@theme/scrolling';
import { SlideshowSelectEvent } from '@theme/events';

// The threshold for determining visibility of slides.
const SLIDE_VISIBLITY_THRESHOLD = 0.7;
const DRAG_INTENT_EVENT = 'theme:drag-intent';

/**
 * Shared viewport observer manager for lazy scroll enablement.
 *
 * Limit the number of compositor layers created by slideshows by only enabling scrolling when the slideshow is in the viewport.
 * Resolves known issues with iOS Safari where too many composition layers will crash the page.
 * When a slideshow is NOT in the viewport, it has overflow: hidden (no compositor layer).
 * When a slideshow enters the viewport, the [in-viewport] attribute is added, enabling scrolling.
 */
class SlideshowViewportObserver {
  /** @type {SlideshowViewportObserver | null} */
  static #instance = null;

  /** @type {IntersectionObserver | null} */
  #observer = null;

  /**
   * Gets the singleton instance
   * @returns {SlideshowViewportObserver}
   */
  static getInstance() {
    if (!this.#instance) {
      this.#instance = new SlideshowViewportObserver();
    }
    return this.#instance;
  }

  /**
   * Registers a slideshow to be observed for viewport visibility
   * @param {Slideshow} slideshow - The slideshow to observe
   */
  observe(slideshow) {
    if (!this.#observer) {
      this.#observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            const slideshowElement = /** @type {Slideshow} */ (entry.target);
            if (entry.isIntersecting) {
              slideshowElement.setAttribute('in-viewport', '');
            } else {
              slideshowElement.removeAttribute('in-viewport');
            }
          }
        },
        {
          rootMargin: '100px',
        }
      );
    }

    this.#observer.observe(slideshow);
  }

  /**
   * Unregisters a slideshow from viewport observation
   * @param {Slideshow} slideshow - The slideshow to unobserve
   */
  unobserve(slideshow) {
    this.#observer?.unobserve(slideshow);
    slideshow.removeAttribute('in-viewport');
  }
}

/**
 * Slideshow custom element that allows sliding between content.
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} scroller
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement[]} [slides]
 * @property {HTMLElement} [current]
 * @property {HTMLElement[]} [thumbnails]
 * @property {HTMLElement[]} [dots]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class Slideshow extends Component {
  static get observedAttributes() {
    return ['initial-slide'];
  }

  /**
   * @param {string} name
   * @param {string} oldValue
   * @param {string} newValue
   */
  attributeChangedCallback(name, oldValue, newValue) {
    // Collection page filtering will Morph slideshow galleries in place, updating
    // the slideshow[initial-slide] and slideshow-slide[hidden] attributes.
    // We need to re-select() the slide after the morph is complete, but not before
    // slideshow-slide elements have their [hidden] attribute updated.
    if (name === 'initial-slide' && oldValue !== newValue) {
      queueMicrotask(() => {
        // Only select if the component is connected and initialized
        if (!this.isConnected || !this.#scroll || !this.refs.slides) return;
        const index = parseInt(newValue, 10) || 0;
        const slide_id = this.refs.slides[index]?.getAttribute('slide-id');
        if (slide_id) {
          this.select({ id: slide_id }, undefined, { animate: false });
        }
      });
    }
  }

  requiredRefs = ['scroller'];

  #connectionRevision = 0;
  #productGalleryReady = false;
  /** @type {{ input: number|string|{id: string}, event: Event, options: {animate?: boolean} } | undefined} */
  #pendingProductSelection;

  async connectedCallback() {
    super.connectedCallback();
    const revision = ++this.#connectionRevision;
    this.#productGalleryReady = false;

    // Register with shared viewport observer for lazy scroll enablement.
    // This prevents iOS Safari crashes caused by too many compositor layers.
    SlideshowViewportObserver.getInstance().observe(this);

    // Wait for any in-progress view transitions to finish
    if (viewTransition.current) {
      // Cancelling a page animation must not leave gallery controls unready.
      try { await viewTransition.current; } catch { /* Continue setup after cancellation. */ }
      // It's possible that the slideshow was disconnected before the view transition finished
      if (!this.isConnected || revision !== this.#connectionRevision) return;
    }

    const slideCount = this.slides?.length || 0;
    slideCount <= 1 ? this.#setupSlideshowWithoutControls() : this.#setupSlideshow();
    if (slideCount <= 1 && this.#isProductGallery) this.#resumePendingProductSelection();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    ++this.#connectionRevision;
    this.#productGalleryReady = false;
    this.#pendingProductSelection = undefined;
    this.#desktopDragController?.abort();
    clearTimeout(this.#resourceResumeTimeout);
    this.#dragging = false;
    this.#productGalleryGestureController?.abort();
    this.#clearProductGalleryFade();
    this.#clearResourceLoopBridges();

    // Unregister from shared viewport observer
    SlideshowViewportObserver.getInstance().unobserve(this);

    if (this.#scroll) {
      const { scroller } = this.refs;
      scroller.removeEventListener('mousedown', this.#handleMouseDown);
      scroller.removeEventListener('pointerdown', this.#handleResourceTouchPointerDown);
      this.#scroll.destroy();
      this.#scroll = undefined;
    }

    const slideCount = this.slides?.length || 0;
    if (slideCount > 1) {
      this.removeEventListener('mouseenter', this.suspend);
      this.removeEventListener('mouseleave', this.resume);
      this.removeEventListener('pointerenter', this.#handlePointerEnter);
      document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
    }

    if (this.#resizeObserver) {
      this.#resizeObserver.disconnect();
    }

    if (this.#intersectionObserver) {
      this.#intersectionObserver.disconnect();
      this.#intersectionObserver = null;
    }
  }

  /** Indicates whether an outer slideshow/carousel owns dragging this card. */
  get isNested() {
    // The homepage's product selector is a separate custom element, not a
    // slideshow-component. Its card galleries must also disable native swipes.
    return this.parentElement?.closest('slideshow-component, product-character-carousel') != null;
  }

  get initialSlide() {
    return this.refs.slides?.[this.initialSlideIndex];
  }

  /**
   * Selects a slide based on the input index.
   * @param {number|string|{id: string}} input - The index or id of the slide to select.
   * @param {Event} [event] - The event that triggered the selection.
   * @param {Object} [options] - The options for the selection.
   * @param {boolean} [options.animate=true] - Whether to animate the selection.
   */
  async select(input, event, options = {}) {
    if (this.#isProductGallery && !this.#productGalleryReady) {
      // A real click may arrive before transition/setup finishes. Keep the
      // latest choice instead of consuming it; replay the selection API, not
      // a synthetic click, after initial positioning can no longer overwrite it.
      if (event && this.isConnected) {
        event.preventDefault();
        this.#pendingProductSelection = { input, event, options };
      }
      return;
    }
    if (this.#isProductGallery && this.#productZoomActive) {
      // Horizon routes controls during document capture, before the gallery's
      // click listener. Finish zooming out before cloning the outgoing photo
      // for its crossfade; only the latest pending selection should continue.
      const control = event?.target instanceof Element &&
        event.target.closest('slideshow-controls, slideshow-arrows, .slideshow-controls__arrows');
      if (!control) return;
      const gallery = this.closest('media-gallery');
      const zoom = gallery?.querySelector('product-photo-zoom')?.controller;
      event?.preventDefault();
      if (zoom?.prepareNavigation) {
        if (!(await zoom.prepareNavigation())) return;
      } else {
        gallery?.resetPhotoZoom?.();
      }
    }
    if (this.#disabled || !this.refs.slides?.length) return;
    if (!this.#scroll) {
      if (this.#isProductGallery) this.#focusProductPhoto(event);
      return;
    }

    // Store the actual current slide before any mutations
    const currentSlide = this.slides?.[this.current];

    for (const slide of this.refs.slides) {
      if (slide.hasAttribute('reveal')) {
        slide.removeAttribute('reveal');
        slide.setAttribute('aria-hidden', 'true');
      }
    }

    // Figure out the raw desired index (could be -1 if user is on first slide and clicks prev)
    let requestedIndex = (() => {
      if (typeof input === 'number') return input;
      if (typeof input === 'string') return parseInt(input, 10);
      if ('id' in input) {
        const requestedSlide = this.refs.slides.find((slide) => slide.getAttribute('slide-id') == input.id);

        if (!requestedSlide || !this.slides) return;

        // Force the slide to be revealed if it is hidden
        if (requestedSlide.hasAttribute('hidden')) {
          requestedSlide.setAttribute('reveal', '');
          requestedSlide.setAttribute('aria-hidden', 'false');
        }

        return this.slides.indexOf(requestedSlide);
      }
    })();

    const { current } = this;
    const { slides } = this;

    // Guard checks: no slides, invalid index, or selecting the same slide
    if (!slides?.length || requestedIndex === undefined || isNaN(requestedIndex)) return;

    // Run after a zoom return releases the page, but before image loading or
    // the same-photo early return. Re-selecting a thumbnail should focus it too.
    if (this.#isProductGallery) this.#focusProductPhoto(event);

    const requestedSlideElement = slides?.[requestedIndex];
    if (currentSlide === requestedSlideElement) {
      if (this.#isProductGallery) this.#clearProductGalleryFade();
      return;
    }

    if (!this.infinite) requestedIndex = clamp(requestedIndex, 0, slides.length - 1);

    event?.preventDefault();

    const { animate = true } = options;
    const lastIndex = slides.length - 1;

    // Decide the actual target index (clamp for infinite loop)
    let index = requestedIndex;
    if (requestedIndex < 0) index = lastIndex;
    else if (requestedIndex > lastIndex) index = 0;

    const isAdjacentSlide = Math.abs(index - current) <= 1 && requestedIndex >= 0 && requestedIndex <= lastIndex;
    const { visibleSlides } = this;
    const instant = prefersReducedMotion() || !animate;

    if (this.#isProductGallery) {
      await this.#selectProductGallery(slides[index], index, currentSlide, event, instant);
      return;
    }
    const wrapsResource = this.#isResourceCarousel && this.infinite && (requestedIndex < 0 || requestedIndex > lastIndex);
    if (wrapsResource && !instant) {
      const backwards = requestedIndex < 0;
      const connectedBridges = this.#resourceLoopBridges.filter((bridge) => bridge.isConnected);
      let preparedBridges;

      // Trailing copies already begin with the first product, so they provide
      // the exact next card at the forward seam. A backwards arrow needs only
      // the final product directly before the first; using a whole visible page
      // here would make one click travel several products.
      if (!backwards && this.#resourceLoopDirection > 0 && connectedBridges.length) {
        preparedBridges = connectedBridges;
      } else {
        if (connectedBridges.length) this.#clearResourceLoopBridges();
        preparedBridges = this.#createResourceBridges([slides[index]]);
      }

      await this.#loopResourceCarousel(index, backwards, event, preparedBridges);
      return;
    }
    if (!instant && !isAdjacentSlide && visibleSlides.length === 1) {
      this.#disabled = true;
      await this.#scroll.finished; // ensure we're not mid-scroll

      const targetSlide = slides[index];
      if (!targetSlide || !currentSlide) {
        this.#disabled = false;
        return;
      }

      // Create a placeholder in the original DOM position of targetSlide
      const placeholder = document.createElement('slideshow-slide');
      targetSlide.before(placeholder);

      // Decide whether targetSlide goes before or after currentSlide
      // so that we scroll a short distance in the correct direction
      if (requestedIndex < current) {
        currentSlide.before(targetSlide);
      } else {
        currentSlide.after(targetSlide);
      }

      if (current === 0) this.#scroll.to(currentSlide, { instant: true });

      // Once that scroll finishes, restore the DOM
      queueMicrotask(async () => {
        await this.#scroll.finished;
        this.#disabled = false;

        // Restore the slide back to its original position. This triggers a scroll event.
        placeholder.replaceWith(targetSlide);

        // Instantly scroll to the target slide as its position will have changed
        this.#scroll.to(targetSlide, { instant: true });

        // Force Safari to recalculate the timeline state on timeline refresh (after loop)
        requestAnimationFrame(() => {
          this.setAttribute('refreshing-timeline', '');
          requestAnimationFrame(() => {
            this.removeAttribute('refreshing-timeline');
          });
        });
      });
    }

    const slide = slides[index];
    if (!slide) return;

    if (this.#isResourceCarousel && this.infinite) {
      // A card near the end cannot align with the left edge while the real
      // track is shorter than the viewport remainder. Stage trailing copies
      // before scrolling so every one-card step has a reachable destination.
      if (this.#resourceLoopDirection < 0) this.#clearResourceLoopBridges();
      const { scroller } = this.refs;
      const slidePosition = slide.getBoundingClientRect().left -
        scroller.getBoundingClientRect().left + scroller.scrollLeft;
      if (slidePosition > scroller.scrollWidth - scroller.clientWidth) this.#stageResourceLoop(1);
    }

    const previousIndex = this.current;

    slide.setAttribute('aria-hidden', 'false');

    if (this.#scroll) {
      this.#scroll.to(slide, { instant });
    }

    this.current = this.slides?.indexOf(slide) || 0;

    this.#centerSelectedThumbnail(index, instant ? 'instant' : 'smooth');

    this.dispatchEvent(
      new SlideshowSelectEvent({
        index,
        previousIndex,
        userInitiated: event != null,
        trigger: 'select',
        slide,
        id: slide.getAttribute('slide-id'),
      })
    );
  }

  #productGalleryFadeOverlay;
  #productGalleryFadeIn;
  #productGalleryFadeOut;
  #productImageRequest;
  #productImageStatus;

  #resumePendingProductSelection() {
    this.#productGalleryReady = true;
    const pending = this.#pendingProductSelection;
    this.#pendingProductSelection = undefined;
    if (!pending) return false;
    void this.select(pending.input, pending.event, pending.options).catch(error => {
      if (this.isConnected) console.warn('[slideshow] Pending photo selection failed:', error);
    });
    return true;
  }

  /** Bring the main product photo into view only for explicit thumbnail clicks. */
  #focusProductPhoto(event) {
    if (!(event?.target instanceof Element) || this.closest('quick-add-dialog, dialog')) return;
    const thumbnail = event.target.closest('.slideshow-controls__thumbnail');
    if (!thumbnail || !this.refs.thumbnails?.includes(thumbnail)) return;
    const photo = this.refs.scroller;
    if (!(photo instanceof HTMLElement) || !this.isConnected) return;

    // Desktop scrolls the page wrapper; mobile scrolls the document. Do not
    // use scrollIntoView(), which can also move the gallery/thumbnail tracks.
    const wrapper = this.closest('.page-wrapper');
    const scroller = wrapper && /auto|scroll/.test(getComputedStyle(wrapper).overflowY)
      ? wrapper : document.scrollingElement;
    if (!(scroller instanceof HTMLElement)) return;
    const isDocument = scroller === document.scrollingElement;
    const viewportTop = isDocument ? (window.visualViewport?.offsetTop || 0)
      : scroller.getBoundingClientRect().top + scroller.clientTop;
    const viewportHeight = isDocument ? (window.visualViewport?.height || window.innerHeight) : scroller.clientHeight;
    const bounds = photo.getBoundingClientRect();
    if (!bounds.width || !bounds.height) return;
    const header = document.getElementById('header-component');
    const headerBounds = header?.getBoundingClientRect();
    // Reserve sticky height even while hidden: scrolling up brings it back.
    const headerInset = headerBounds ? (header.hasAttribute('sticky') ? headerBounds.height
      : Math.max(0, headerBounds.bottom - viewportTop)) : 0;
    const availableHeight = Math.max(0, viewportHeight - headerInset - 16);
    const photoTop = viewportTop + headerInset + 8 + Math.max(0, (availableHeight - bounds.height) / 2);
    const top = clamp(scroller.scrollTop + bounds.top - photoTop, 0, Math.max(0, scroller.scrollHeight - scroller.clientHeight));
    if (Math.abs(top - scroller.scrollTop) < 2) return;
    scroller.scrollTo({ top, behavior: prefersReducedMotion() ? 'instant' : 'smooth' });
  }

  #clearProductGalleryFade() {
    this.#productImageRequest?.abort();
    this.#productImageRequest = undefined;
    this.#productImageStatus?.remove();
    this.#productImageStatus = undefined;
    this.removeAttribute('aria-busy');
    this.#clearProductGalleryMotion();
  }

  #clearProductGalleryMotion() {
    this.#productGalleryFadeIn?.cancel();
    this.#productGalleryFadeOut?.cancel();
    this.#productGalleryFadeOverlay?.remove();
    this.#productGalleryFadeIn = undefined;
    this.#productGalleryFadeOut = undefined;
    this.#productGalleryFadeOverlay = undefined;
  }

  /** Change product photos directly, with a crossfade instead of track movement. */
  async #selectProductGallery(slide, index, currentSlide, event, instant) {
    if (!slide) return;
    this.#clearProductGalleryFade();
    if (slide === currentSlide) return;

    const container = this.refs.slideshowContainer;
    const oldImage = currentSlide?.querySelector('.product-media__image');
    // Mount before waiting for the photo, even if the small loader module has
    // not upgraded yet. The current photo stays visible while it downloads.
    const deferredPhoto = slide.querySelector('template[data-product-display-image]');
    if (deferredPhoto instanceof HTMLTemplateElement) {
      deferredPhoto.replaceWith(deferredPhoto.content.cloneNode(true));
    }
    slide.querySelector('product-image-loader')?.load?.();
    const newImage = slide.querySelector('.product-media__image');
    if (newImage instanceof HTMLImageElement && (!newImage.complete || !newImage.naturalWidth)) {
      const controller = new AbortController();
      this.#productImageRequest = controller;
      const status = document.createElement('div');
      status.className = 'product-gallery-load-status';
      status.setAttribute('role', 'status');
      status.textContent = Theme.translations.product_image_loading;
      container.append(status);
      this.#productImageStatus = status;
      this.setAttribute('aria-busy', 'true');
      newImage.loading = 'eager';
      try {
        await new Promise((resolve, reject) => {
          const cleanup = () => {
            clearTimeout(timeout);
            newImage.removeEventListener('load', loaded);
            newImage.removeEventListener('error', failed);
            controller.signal.removeEventListener('abort', aborted);
          };
          const loaded = () => {
            if (!newImage.complete || !newImage.naturalWidth) return;
            cleanup(); resolve();
          };
          const failed = () => { cleanup(); reject(new Error('Product image unavailable')); };
          const aborted = () => { cleanup(); reject(new DOMException('Superseded', 'AbortError')); };
          const timeout = setTimeout(failed, 15000);
          newImage.addEventListener('load', loaded);
          newImage.addEventListener('error', failed);
          controller.signal.addEventListener('abort', aborted, { once: true });
          loaded();
        });
        // A load with natural dimensions is paintable even when decode rejects
        // because a responsive source changed. Never use a timer as readiness.
        if (controller.signal.aborted || !this.isConnected) return;
      } catch (error) {
        if (controller.signal.aborted || !this.isConnected) return;
        this.removeAttribute('aria-busy');
        const message = document.createElement('span');
        message.textContent = Theme.translations.product_image_error;
        const retry = document.createElement('button');
        retry.type = 'button';
        retry.className = 'button button-secondary';
        retry.textContent = Theme.translations.product_retry;
        retry.addEventListener('click', () => {
          // Reassign the display source to allow retry after a failed request.
          newImage.src = newImage.getAttribute('src');
          void this.#selectProductGallery(slide, index, currentSlide, event, instant);
        }, { once: true });
        status.replaceChildren(message, retry);
        return;
      }
      this.#clearProductGalleryFade();
    }
    const canFade = !instant && container && oldImage instanceof HTMLImageElement &&
      newImage instanceof HTMLImageElement && oldImage.complete && oldImage.naturalWidth > 0;
    let overlay;
    if (canFade) {
      const imageBounds = oldImage.getBoundingClientRect();
      const containerBounds = container.getBoundingClientRect();
      const imageStyle = getComputedStyle(oldImage);
      overlay = /** @type {HTMLImageElement} */ (oldImage.cloneNode());
      overlay.removeAttribute('srcset');
      overlay.src = oldImage.currentSrc;
      overlay.className = 'product-gallery-fade-overlay';
      overlay.alt = '';
      overlay.setAttribute('aria-hidden', 'true');
      overlay.loading = 'eager';
      overlay.style.left = `${imageBounds.left - containerBounds.left}px`;
      overlay.style.top = `${imageBounds.top - containerBounds.top}px`;
      overlay.style.width = `${imageBounds.width}px`;
      overlay.style.height = `${imageBounds.height}px`;
      overlay.style.objectFit = imageStyle.objectFit;
      overlay.style.objectPosition = imageStyle.objectPosition;
      overlay.style.background = imageStyle.backgroundColor;
      container.append(overlay);
      this.#productGalleryFadeOverlay = overlay;
    }

    const previousIndex = this.current;
    slide.setAttribute('aria-hidden', 'false');
    currentSlide?.setAttribute('aria-hidden', 'true');
    this.#scroll.to(slide, { instant: true });
    this.current = index;
    this.#centerSelectedThumbnail(index, instant ? 'instant' : 'smooth');
    this.dispatchEvent(new SlideshowSelectEvent({
      index, previousIndex, userInitiated: event != null, trigger: 'select',
      slide, id: slide.getAttribute('slide-id'),
    }));

    if (!overlay) return;
    const gallery = this.closest('media-gallery');
    const configuredDuration = Number.parseFloat(
      getComputedStyle(gallery || this).getPropertyValue('--product-gallery-transition-duration')
    );
    const duration = Number.isFinite(configuredDuration) ? Math.max(0, configuredDuration) : 350;
    newImage.loading = 'eager';
    const fade = () => {
      if (this.#productGalleryFadeOverlay !== overlay || !this.isConnected) return;
      this.#productGalleryFadeIn = newImage.animate([{ opacity: 0 }, { opacity: 1 }], {
        duration, easing: 'ease-in-out',
      });
      this.#productGalleryFadeOut = overlay.animate([{ opacity: 1 }, { opacity: 0 }], {
        duration, easing: 'ease-in-out',
      });
      this.#productGalleryFadeOut.finished.then(() => {
        if (this.#productGalleryFadeOverlay === overlay) this.#clearProductGalleryFade();
      }).catch(() => {});
    };
    if (newImage.complete && newImage.naturalWidth > 0) fade();
  }

  /** Return the image that visually represents a resource card at the seam. */
  #resourceCardImage(slide) {
    const gallery = slide.querySelector('.card-gallery');
    if (!gallery) return slide.querySelector('.product-media__image') || slide.querySelector('img');

    const currentSlide =
      gallery.querySelector('slideshow-slide[aria-hidden="false"]:not([hidden])') ||
      gallery.querySelector('slideshow-slide:not([hidden])');
    return currentSlide?.querySelector('.product-media__image') || currentSlide?.querySelector('img') ||
      gallery.querySelector('.product-media__image') || gallery.querySelector('img');
  }

  /**
   * Reduce a copied product gallery to the frame the shopper can actually see.
   * A seam copy exists for a fraction of a drag, so its other media and controls
   * only add layout, decoding, and custom-element work without changing the view.
   */
  #reduceResourceBridgeGallery(source, clone) {
    const sourceGallery = source.querySelector('.card-gallery');
    const cloneGallery = clone.querySelector('.card-gallery');
    if (!sourceGallery || !cloneGallery) return;

    const sourceSlide =
      sourceGallery.querySelector('slideshow-slide[aria-hidden="false"]:not([hidden])') ||
      sourceGallery.querySelector('slideshow-slide:not([hidden])');
    const sourceSlideId = sourceSlide?.getAttribute('slide-id');
    const cloneSlides = [...cloneGallery.querySelectorAll('slideshow-slide')];
    const visibleClone =
      (sourceSlideId && cloneSlides.find((slide) => slide.getAttribute('slide-id') === sourceSlideId)) ||
      cloneSlides.find((slide) => !slide.hasAttribute('hidden')) ||
      cloneSlides[0];

    cloneSlides.forEach((slide) => {
      if (slide !== visibleClone) slide.remove();
    });
    visibleClone?.removeAttribute('hidden');
    visibleClone?.removeAttribute('reveal');
    visibleClone?.setAttribute('aria-hidden', 'false');
    if (visibleClone) visibleClone.style.contentVisibility = 'visible';

    cloneGallery.querySelectorAll('slideshow-arrows, slideshow-controls').forEach((controls) => controls.remove());
    const scroller = cloneGallery.querySelector('slideshow-slides');
    if (scroller instanceof HTMLElement) {
      scroller.removeAttribute('tabindex');
      scroller.style.overflow = 'hidden';
      scroller.style.scrollBehavior = 'auto';
      scroller.style.scrollSnapType = 'none';
    }
  }

  /**
   * Replace behavior-bearing custom elements with plain wrappers before a copy
   * reaches the document. Classes, styles, and children stay intact, but the
   * product card, quick add, price, and nested gallery cannot initialize again.
   */
  #makeResourceBridgeStatic(clone) {
    const structuralTags = new Set(['slideshow-container', 'slideshow-slides', 'slideshow-slide']);
    const customElements = [...clone.querySelectorAll('*')].filter(
      (element) => element.localName.includes('-') && !structuralTags.has(element.localName)
    );

    customElements.reverse().forEach((element) => {
      const replacement = document.createElement('div');
      for (const attribute of [...element.attributes]) {
        replacement.setAttribute(attribute.name, attribute.value);
      }
      replacement.dataset.resourceLoopStaticElement = element.localName;
      while (element.firstChild) replacement.append(element.firstChild);

      if (element.localName === 'slideshow-component') {
        replacement.classList.add('resource-loop-static-slideshow');
        replacement.style.position = 'relative';
        replacement.style.display = 'flex';
        replacement.style.flexDirection = 'column';
        replacement.style.width = '100%';
      }
      element.replaceWith(replacement);
    });
  }

  /** Build lightweight, inert visual card duplicates for a resource-carousel seam. */
  #createResourceBridges(source) {
    return source.map((slide) => {
      const clone = /** @type {HTMLElement} */ (slide.cloneNode(true));
      clone.removeAttribute('ref');
      clone.removeAttribute('slide-id');
      clone.setAttribute('aria-hidden', 'true');
      clone.setAttribute('data-resource-loop-bridge', '');
      clone.style.contentVisibility = 'visible';
      clone.inert = true;
      this.#reduceResourceBridgeGallery(slide, clone);
      clone.querySelectorAll('*').forEach((node) => {
        for (const attribute of [...node.attributes]) {
          if (attribute.name === 'id' || attribute.name === 'ref' ||
            attribute.name === 'data-view-transition-type' || attribute.name.startsWith('on:')) {
            node.removeAttribute(attribute.name);
          }
        }
      });
      clone.querySelectorAll('script, template').forEach((element) => element.remove());

      const sourceImage = this.#resourceCardImage(slide);
      const cloneImage = this.#resourceCardImage(clone);
      clone.querySelectorAll('img').forEach((image) => {
        image.decoding = 'async';
        image.removeAttribute('fetchpriority');
        image.loading = image === cloneImage ? 'eager' : 'lazy';
      });
      if (cloneImage) {
        const loadedSource = sourceImage?.currentSrc || sourceImage?.src;
        if (loadedSource) {
          cloneImage.removeAttribute('srcset');
          cloneImage.removeAttribute('sizes');
          cloneImage.src = loadedSource;
        }
        if (!cloneImage.complete) void cloneImage.decode?.().catch(() => {});
      }

      this.#makeResourceBridgeStatic(clone);
      return clone;
    });
  }

  /** Size the temporary lane to one viewport plus one safety card. */
  #resourceLoopCardCount() {
    const { scroller } = this.refs;
    const slides = this.slides;
    if (!scroller || !slides?.length) return 0;

    const firstRect = slides[0].getBoundingClientRect();
    const secondRect = slides[1]?.getBoundingClientRect();
    const cardPitch = secondRect ? Math.abs(secondRect.left - firstRect.left) : firstRect.width;
    const cardsPerViewport = Math.max(1, Math.ceil(scroller.clientWidth / (cardPitch || scroller.clientWidth)));

    return Math.min(slides.length, cardsPerViewport + 1);
  }

  /** Load only the visible image from cards needed for the upcoming seam. */
  #primeResourceLoopMedia(edgeSlides) {
    if (!this.#isResourceCarousel || !this.infinite) return;
    if (!edgeSlides.length) return;

    new Set(edgeSlides).forEach((slide) => {
      const image = this.#resourceCardImage(slide);
      if (!image) return;
      image.loading = 'eager';
      image.decoding = 'async';
      if (!image.complete) void image.decode?.().catch(() => {});
    });
  }

  /** Cross a resource-carousel seam using temporary inert cards, then rebase. */
  async #loopResourceCarousel(index, backwards, event, preparedBridges) {
    const { scroller } = this.refs;
    const slides = this.slides;
    if (!slides?.length) return;
    const previousIndex = this.current;
    const pageSize = Math.max(1, Math.min(this.visibleSlides.length || 1, slides.length));
    const source = backwards ? slides.slice(-pageSize) : slides.slice(0, pageSize);
    this.#primeResourceLoopMedia(source);
    const bridges = preparedBridges || this.#createResourceBridges(source);
    const target = slides[index];
    if (!target || !bridges.length) return;

    this.#disabled = true;
    const originalOverflowAnchor = scroller.style.overflowAnchor;
    const originalSnap = scroller.style.scrollSnapType;
    try {
      await this.#scroll.finished;
      this.#scroll.snap = false;
      scroller.style.overflowAnchor = 'none';
      this.setAttribute('resource-looping', '');
      const before = scroller.scrollLeft;
      if (!bridges[0]?.isConnected && backwards) {
        const first = slides[0];
        const oldLeft = first.getBoundingClientRect().left;
        const fragment = document.createDocumentFragment();
        bridges.forEach((bridge) => fragment.append(bridge));
        scroller.prepend(fragment);
        this.#scroll.to(before + first.getBoundingClientRect().left - oldLeft, { instant: true });
      } else if (!bridges[0]?.isConnected) {
        bridges.forEach((bridge) => scroller.append(bridge));
      }

      // The final bridge can sit slightly beyond the browser's maximum scroll
      // position because of end padding. Target the reachable edge explicitly;
      // asking the browser to smooth-scroll past it creates no scroll event and
      // leaves the loop waiting forever instead of performing the invisible rebase.
      const bridgePosition = bridges[0].getBoundingClientRect().left -
        scroller.getBoundingClientRect().left + scroller.scrollLeft;
      const bridgeTarget = backwards
        ? 0
        : clamp(bridgePosition, 0, scroller.scrollWidth - scroller.clientWidth);
      this.#scroll.to(bridgeTarget);
      await this.#scroll.finished;
      const bridgeViewportOffset = bridges[0].getBoundingClientRect().left - scroller.getBoundingClientRect().left;
      bridges.forEach((bridge) => bridge.remove());
      const targetPosition = target.getBoundingClientRect().left -
        scroller.getBoundingClientRect().left + scroller.scrollLeft;
      const equivalentTarget = clamp(
        targetPosition - bridgeViewportOffset,
        0,
        scroller.scrollWidth - scroller.clientWidth
      );
      this.#scroll.to(equivalentTarget, { instant: true });
      this.current = index;
      this.dispatchEvent(new SlideshowSelectEvent({
        index, previousIndex, userInitiated: event != null, trigger: 'scroll',
        slide: target, id: target.getAttribute('slide-id'),
      }));
    } finally {
      bridges.forEach((bridge) => bridge.remove());
      if (bridges.some((bridge) => this.#resourceLoopBridges.includes(bridge))) {
        this.#resourceLoopBridges = [];
        this.#resourceLoopDirection = 0;
        this.#resourceLoopOriginalStart = 0;
        this.#resourceLoopOriginalEnd = 0;
      }
      scroller.style.overflowAnchor = originalOverflowAnchor;
      scroller.style.scrollSnapType = this.#isResourceCarousel ? 'none' : originalSnap;
      this.removeAttribute('resource-looping');
      this.#disabled = false;
    }
  }

  /**
   * Advances to the next slide.
   * @param {Event} [event] - The event that triggered the next slide.
   * @param {Object} [options] - The options for the next slide.
   * @param {boolean} [options.animate=true] - Whether to animate the next slide.
   */
  async next(event, options) {
    event?.preventDefault();
    const index = this.#isResourceCarousel || this.#isProductGallery ? this.current + 1 : this.nextIndex;

    if (this.#isResourceCarousel && event) {
      clearTimeout(this.#resourceResumeTimeout);
      this.suspend();
    }

    try {
      await this.select(index, event, options);
    } finally {
      if (this.#isResourceCarousel && event && !this.#dragging && this.isConnected) this.resume();
    }
  }

  /**
   * Goes back to the previous slide.
   * @param {Event} [event] - The event that triggered the previous slide.
   * @param {Object} [options] - The options for the previous slide.
   * @param {boolean} [options.animate=true] - Whether to animate the previous slide.
   */
  async previous(event, options) {
    event?.preventDefault();
    const index = this.#isResourceCarousel || this.#isProductGallery ? this.current - 1 : this.previousIndex;

    if (this.#isResourceCarousel && event) {
      clearTimeout(this.#resourceResumeTimeout);
      this.suspend();
    }

    try {
      await this.select(index, event, options);
    } finally {
      if (this.#isResourceCarousel && event && !this.#dragging && this.isConnected) this.resume();
    }
  }

  /**
   * Starts automatic slide playback.
   * @param {number} [interval] - The time interval in seconds between slides.
   */
  play(interval = this.autoplayInterval) {
    if (this.#interval) return;

    this.paused = false;

    this.#interval = setInterval(() => {
      if ((!this.#isResourceCarousel && this.matches(':hover')) || document.hidden) return;

      if (this.#isResourceCarousel) void this.select(this.current + 1);
      else void this.next();
    }, interval);
  }

  /**
   * Pauses automatic slide playback.
   */
  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(value) {
    if (value) {
      this.setAttribute('paused', '');
    } else {
      this.removeAttribute('paused');
    }
  }

  /**
   * Suspends automatic slide playback.
   */
  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  /**
   * Resumes automatic slide playback if autoplay is enabled.
   */
  resume() {
    if (!this.autoplay || this.paused) return;

    this.pause();
    this.play();
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);

    if (Number.isNaN(value)) return undefined;

    return value * 1000;
  }

  /**
   * The current slide index.
   * @type {number}
   */
  #current = 0;

  get current() {
    return this.#current;
  }

  /**
   * Sets the current slide index and update the DOM
   * @type {number}
   */
  set current(value) {
    const { current, thumbnails, dots, slides, previous, next } = this.refs;

    this.#current = value;

    if (current) current.textContent = `${value + 1}`;

    for (const controls of [thumbnails, dots]) {
      controls?.forEach((el, i) => {
        // Separate desktop/mobile rails repeat the same photo indices.
        const index = el.hasAttribute('data-thumbnail-index') ? Number(el.dataset.thumbnailIndex) : i;
        el.setAttribute('aria-selected', `${index === value}`);
      });
    }

    if (previous) previous.disabled = Boolean(!this.infinite && value === 0);
    if (next) next.disabled = Boolean(!this.infinite && slides && this.nextIndex >= slides.length);
  }

  get infinite() {
    return this.getAttribute('infinite') != null;
  }

  get visibleSlides() {
    return this.#visibleSlides;
  }

  get previousIndex() {
    const { current, visibleSlides } = this;
    const modifier = visibleSlides.length > 1 ? visibleSlides.length : 1;

    return current - modifier;
  }

  get nextIndex() {
    const { current, visibleSlides } = this;
    const modifier = visibleSlides.length > 1 ? visibleSlides.length : 1;

    return current + modifier;
  }

  get atStart() {
    const { current, slides } = this;

    return slides?.length ? current === 0 : false;
  }

  get atEnd() {
    const { current, slides } = this;

    return slides?.length ? current === slides.length - 1 : false;
  }

  /**
   * Sets the disabled attribute.
   * @param {boolean} value - The value to set the disabled attribute to.
   */
  set disabled(value) {
    this.setAttribute('disabled', String(value));
  }
  /**
   * Whether the slideshow is disabled.
   * @type {boolean}
   */
  get disabled() {
    return (
      this.getAttribute('disabled') === 'true' || (this.hasAttribute('mobile-disabled') && !mediaQueryLarge.matches)
    );
  }

  /**
   * Indicates whether the slideshow is temporarily disabled (e.g., during infinite loop transition).
   * @type {boolean}
   */
  #disabled = false;

  /**
   * The interval ID for automatic playback.
   * @type {number|undefined}
   */
  #interval = undefined;

  /**
   * The Scroller instance that manages scrolling.
   * @type {Scroller}
   */
  #scroll;

  /**
   * The ResizeObserver instance for monitoring scroller size changes
   * @type {ResizeObserver}
   */
  #resizeObserver;

  /**
   * IntersectionObserver for efficient visibility tracking of slides
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /**
   * Cached visible slides result from IntersectionObserver
   * @type {HTMLElement[]}
   */
  #visibleSlides = [];

  /**
   * Setup the slideshow without controls for zero or one slides
   */
  #setupSlideshowWithoutControls() {
    this.current = 0;
    if (this.hasAttribute('auto-hide-controls')) {
      const { slideshowControls } = this.refs;
      if (slideshowControls instanceof HTMLElement) {
        slideshowControls.hidden = true;
      }
    }

    if (this.refs.slides?.[0]) {
      this.refs.slides[0].setAttribute('aria-hidden', 'false');
    }
  }

  /**
   * Setup the slideshow with controls for when there are multiple slides
   */
  #setupSlideshow() {
    const revision = this.#connectionRevision;
    // Setup IntersectionObserver first for efficient visibility tracking
    this.#setupIntersectionObserver();

    // Setup the scroll instance
    const { scroller } = this.refs;
    this.#scroll = new Scroller(scroller, {
      onScroll: this.#handleScroll,
      onScrollStart: this.#onTransitionInit,
      onScrollEnd: this.#onTransitionEnd,
    });

    scroller.addEventListener('mousedown', this.#handleMouseDown);
    if (this.#isProductGallery) this.#setupProductGalleryGestures(scroller);
    if (this.#isResourceCarousel) scroller.addEventListener('pointerdown', this.#handleResourceTouchPointerDown);

    if (!this.#isResourceCarousel) {
      this.addEventListener('mouseenter', this.suspend);
      this.addEventListener('mouseleave', this.resume);
    }
    this.addEventListener('pointerenter', this.#handlePointerEnter);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);

    this.#updateControlsVisibility();

    this.disabled = this.isNested || this.disabled;

    this.resume();

    this.current = this.initialSlideIndex;

    // Batch reads and writes to the DOM
    scheduler.schedule(() => {
      if (!this.isConnected || revision !== this.#connectionRevision) return;
      let visibleSlidesAmount = 0;
      const initialSlideId = this.initialSlide?.getAttribute('slide-id');

      // Wait for next frame to ensure layout is fully calculated before setting initial scroll position
      // This prevents race conditions on Safari mobile when section_width is 'full-width'
      requestAnimationFrame(() => {
        if (!this.isConnected || revision !== this.#connectionRevision) return;
        if (this.#isProductGallery) {
          // Establish the initial photo first, including a nonzero featured
          // variant. No delayed initial positioning may overwrite a queued tap.
          const initialPhoto = this.slides?.[this.current];
          if (initialPhoto) this.#scroll.to(initialPhoto, { instant: true });
          visibleSlidesAmount = this.#updateVisibleSlides();
          this.#resumePendingProductSelection();
          return;
        }
        if (this.initialSlideIndex !== 0 && initialSlideId) {
          this.select({ id: initialSlideId }, undefined, { animate: false });
          visibleSlidesAmount = 1;
        } else {
          visibleSlidesAmount = this.#updateVisibleSlides();
          if (visibleSlidesAmount === 0) {
            this.select(0, undefined, { animate: false });
            visibleSlidesAmount = 1;
          }
        }
      });

      this.#resizeObserver = new ResizeObserver(async () => {
        if (viewTransition.current) {
          try { await viewTransition.current; } catch { /* A cancelled animation still permits resizing. */ }
        }
        if (!this.isConnected || revision !== this.#connectionRevision) return;

        if (this.#isProductGallery) {
          // Resize invalidates overlay geometry, not the user's pending photo.
          // Focusing the gallery/header or loading fonts can resize this frame
          // while a first, uncached thumbnail image is still downloading.
          this.#clearProductGalleryMotion();
          const selectedSlide = this.slides?.[this.current];
          if (selectedSlide) this.#scroll.to(selectedSlide, { instant: true });
          this.#updateVisibleSlides();
        }

        if (visibleSlidesAmount > 1) {
          this.#updateVisibleSlides();
        }

        if (this.hasAttribute('auto-hide-controls')) {
          this.#updateControlsVisibility();
        }
      });

      this.#resizeObserver.observe(this.refs.slideshowContainer);
    });
  }

  /**
   * Callback invoked on user initiated scroll to sync the current slide index
   * and emit a slide change event if the index has changed.
   */
  #handleScroll = () => {
    // Product selection is explicit. Resizing or browser zoom must not select
    // another photo merely because the old pixel offset now falls between slides.
    if (this.#isProductGallery) return;
    const previousIndex = this.#current;
    const index = this.#sync();

    if (index === previousIndex) return;

    const slide = this.slides?.[index];
    if (!slide) return;

    this.dispatchEvent(
      new SlideshowSelectEvent({
        index,
        previousIndex,
        userInitiated: true,
        trigger: 'scroll',
        slide,
        id: slide.getAttribute('slide-id'),
      })
    );
  };

  #onTransitionInit = () => {
    this.setAttribute('transitioning', '');
  };

  #onTransitionEnd = () => {
    this.#updateVisibleSlides();
    this.removeAttribute('transitioning');
  };

  /**
   * Synchronizes the scroll position and updates the current slide index.
   * @returns {number} The index of the current slide.
   */
  #sync = () => {
    const { slides } = this;
    if (!slides) return (this.current = 0);

    if (!this.#scroll) return (this.current = 0);

    const visibleSlides = this.visibleSlides;

    if (!visibleSlides.length) return this.current;

    const { axis } = this.#scroll;
    const { scroller } = this.refs;
    const centers = visibleSlides.map((slide) => center(slide, axis));
    const referencePoint = visibleSlides.length > 1 ? scroller.getBoundingClientRect()[axis] : center(scroller, axis);
    const closestCenter = closest(centers, referencePoint);
    const closestVisibleSlide = visibleSlides[centers.indexOf(closestCenter)];

    if (!closestVisibleSlide) return (this.current = 0);

    const index = slides.indexOf(closestVisibleSlide);

    return (this.current = index);
  };

  #dragging = false;
  #desktopDragController;
  #dragGeneration = 0;
  #productGalleryGestureController;
  #resourceLoopBridges = [];
  #resourceLoopDirection = 0;
  #resourceLoopOriginalStart = 0;
  #resourceLoopOriginalEnd = 0;
  #resourceResumeTimeout;

  #scheduleResourceCarouselResume(generation, delay = 1500) {
    clearTimeout(this.#resourceResumeTimeout);
    this.#resourceResumeTimeout = setTimeout(() => {
      this.#resourceResumeTimeout = undefined;
      if (this.#dragging || generation !== this.#dragGeneration || !this.isConnected) return;
      void this.#resumeResourceCarousel(generation).catch(() => {
        // A failed settling pass must never strand an autoplaying reel in its
        // suspended state. The next autoplay step can safely realign it.
        if (!this.#dragging && generation === this.#dragGeneration && this.isConnected) this.resume();
      });
    }, delay);
  }

  async #resumeResourceCarousel(generation) {
    const { scroller } = this.refs;
    const slides = this.slides;
    if (!scroller || !slides?.length || this.#dragging || generation !== this.#dragGeneration) return;
    if (this.#disabled) {
      this.#scheduleResourceCarouselResume(generation, 100);
      return;
    }

    try {
      await this.#scroll.finished;
      if (this.#dragging || generation !== this.#dragGeneration || !this.isConnected) return;

      if (this.#resourceLoopBridges.length) {
        const scrollerLeft = scroller.getBoundingClientRect().left;
        const trackSlides = [...scroller.querySelectorAll('.resource-list__slide')];
        const anchor = trackSlides.reduce((closestSlide, slide) =>
          Math.abs(slide.getBoundingClientRect().left - scrollerLeft) <
            Math.abs(closestSlide.getBoundingClientRect().left - scrollerLeft)
            ? slide
            : closestSlide
        );
        const anchorOffset = anchor.getBoundingClientRect().left - scrollerLeft;
        const bridgeIndex = this.#resourceLoopBridges.indexOf(anchor);
        const anchorIndex = bridgeIndex >= 0
          ? (this.#resourceLoopDirection < 0
            ? slides.length - this.#resourceLoopBridges.length + bridgeIndex
            : bridgeIndex) % slides.length
          : slides.indexOf(anchor);

        this.#clearResourceLoopBridges(false);
        const realAnchor = slides[Math.max(0, anchorIndex)];
        const realAnchorPosition = realAnchor.getBoundingClientRect().left -
          scroller.getBoundingClientRect().left + scroller.scrollLeft;
        // Keep enough trailing track to preserve the anchor after removing copies.
        // Otherwise the browser clamps the scroll to a partial-card end position.
        if (this.infinite && realAnchorPosition - anchorOffset > scroller.scrollWidth - scroller.clientWidth) {
          this.#stageResourceLoop(1);
        }
        scroller.scrollTo({
          left: clamp(realAnchorPosition - anchorOffset, 0, scroller.scrollWidth - scroller.clientWidth),
          behavior: 'instant',
        });
      }

      const scrollerLeft = scroller.getBoundingClientRect().left;
      const targetIndex = slides.reduce((closestIndex, slide, index) =>
        Math.abs(slide.getBoundingClientRect().left - scrollerLeft) <
          Math.abs(slides[closestIndex].getBoundingClientRect().left - scrollerLeft)
          ? index
          : closestIndex
      , 0);
      const targetPosition = slides[targetIndex].getBoundingClientRect().left - scrollerLeft + scroller.scrollLeft;
      if (this.infinite && targetPosition > scroller.scrollWidth - scroller.clientWidth) {
        this.#stageResourceLoop(1);
      }
      this.#scroll.to(targetPosition, { instant: prefersReducedMotion() });
      await this.#scroll.finished;
      if (this.#dragging || generation !== this.#dragGeneration || !this.isConnected) return;
      this.current = targetIndex;
    } finally {
      // Dragging temporarily suspends autoplay. Always restore it after the
      // active gesture settles, including seam-rebase and error paths.
      if (!this.#dragging && generation === this.#dragGeneration && this.isConnected) this.resume();
    }
  }

  #clearResourceLoopBridges(preservePosition = true) {
    const { scroller } = this.refs;
    const leadingWidth = this.#resourceLoopDirection < 0 ? this.#resourceLoopOriginalStart : 0;
    const currentScroll = scroller?.scrollLeft || 0;
    this.#resourceLoopBridges.forEach((bridge) => bridge.remove());
    if (preservePosition && scroller && leadingWidth) {
      scroller.scrollTo({ left: Math.max(0, currentScroll - leadingWidth), behavior: 'instant' });
    }
    this.#resourceLoopBridges = [];
    this.#resourceLoopDirection = 0;
    this.#resourceLoopOriginalStart = 0;
    this.#resourceLoopOriginalEnd = 0;
  }

  #stageResourceLoop(direction) {
    const existing = this.#resourceLoopBridges.filter((bridge) => bridge.isConnected);
    if (existing.length) return existing;
    const { scroller } = this.refs;
    const slides = this.slides;
    if (!scroller || !slides?.length || !this.infinite) return [];

    const bridgeCount = this.#resourceLoopCardCount();
    const source = direction < 0 ? slides.slice(-bridgeCount) : slides.slice(0, bridgeCount);
    this.#primeResourceLoopMedia(source);
    const bridges = this.#createResourceBridges(source);
    const originalEnd = scroller.scrollWidth - scroller.clientWidth;
    if (direction < 0 && bridges[0]) {
      const target = slides[slides.length - bridgeCount];
      const targetPosition = target.getBoundingClientRect().left -
        scroller.getBoundingClientRect().left + scroller.scrollLeft;
      const residualEndOffset = Math.max(0, targetPosition - originalEnd);
      if (residualEndOffset) bridges[0].style.marginInlineStart = `${residualEndOffset}px`;
    }
    const fragment = document.createDocumentFragment();
    bridges.forEach((bridge) => fragment.append(bridge));

    let originalStart = 0;
    if (direction < 0) {
      const first = slides[0];
      const beforeScroll = scroller.scrollLeft;
      const oldLeft = first.getBoundingClientRect().left;
      scroller.prepend(fragment);
      originalStart = first.getBoundingClientRect().left - oldLeft;
      scroller.scrollTo({ left: beforeScroll + originalStart, behavior: 'instant' });
    } else {
      scroller.append(fragment);
    }

    this.#resourceLoopBridges = bridges;
    this.#resourceLoopDirection = direction;
    this.#resourceLoopOriginalStart = originalStart;
    this.#resourceLoopOriginalEnd = originalEnd + originalStart;
    return bridges;
  }

  /** A swipe picks one neighboring photo without moving the full media track. */
  #setupProductGalleryGestures(scroller) {
    this.#productGalleryGestureController?.abort();
    this.#productGalleryGestureController = new AbortController();
    const { signal } = this.#productGalleryGestureController;
    scroller.addEventListener('dragstart', preventDefault, { signal });

    scroller.addEventListener('pointerdown', (event) => {
      if (this.#productZoomActive || (event.pointerType === 'touch' && !event.isPrimary)) return;
      if (event.button !== 0 || this.disabled || this.#disabled || this.slides?.length < 2) return;
      if (!(event.target instanceof Element) ||
        event.target.closest('button, a, input, select, textarea, deferred-media, product-model, model-viewer, video, iframe')) return;

      const startX = event.clientX;
      const startY = event.clientY;
      const pointerId = event.pointerId;
      let horizontal = false;
      const gesture = new AbortController();
      const finish = (endEvent) => {
        gesture.abort();
        if (this.#productZoomActive) return;
        if (!horizontal || endEvent.type !== 'pointerup') return;
        const delta = endEvent.clientX - startX;

        // A swipe must not become tap-to-zoom, but a fresh press on a photo
        // control (including Reset) is a separate, intentional interaction.
        const clearClickGuard = () => {
          this.removeEventListener('click', suppressClick, true);
          this.removeEventListener('pointerdown', clearClickGuard, true);
        };
        const suppressClick = (click) => {
          if (click.detail === 0) return;
          click.preventDefault();
          click.stopImmediatePropagation();
          clearClickGuard();
        };
        this.addEventListener('click', suppressClick, true);
        this.addEventListener('pointerdown', clearClickGuard, { capture: true, once: true });
        setTimeout(clearClickGuard, 300);
        if (Math.abs(delta) < Math.min(64, Math.max(36, scroller.clientWidth * 0.08))) return;
        void this.select(this.current + (delta < 0 ? 1 : -1), endEvent);
      };

      window.addEventListener('pointermove', (moveEvent) => {
        if (this.#productZoomActive) { gesture.abort(); return; }
        if (moveEvent.pointerId !== pointerId) return;
        const deltaX = moveEvent.clientX - startX;
        const deltaY = moveEvent.clientY - startY;
        if (!horizontal && Math.abs(deltaX) > 8 && Math.abs(deltaX) > Math.abs(deltaY) * 1.2) {
          horizontal = true;
        }
        if (horizontal && moveEvent.cancelable) moveEvent.preventDefault();
      }, { signal: gesture.signal });
      window.addEventListener('pointerup', (endEvent) => {
        if (endEvent.pointerId === pointerId) finish(endEvent);
      }, { signal: gesture.signal });
      window.addEventListener('pointercancel', (endEvent) => {
        if (endEvent.pointerId === pointerId) finish(endEvent);
      }, { signal: gesture.signal });
      // A second finger cancels the swipe immediately, before viewport scale
      // or touch handlers update. Neither finger may select the next photo.
      window.addEventListener('pointerdown', (nextEvent) => {
        if (nextEvent.pointerType === 'touch' && nextEvent.pointerId !== pointerId) gesture.abort();
      }, { signal: gesture.signal });
      const abortGesture = () => gesture.abort();
      signal.addEventListener('abort', abortGesture, { once: true });
      gesture.signal.addEventListener('abort', () => signal.removeEventListener('abort', abortGesture), { once: true });
    }, { signal });
  }

  get #isProductGallery() {
    return Boolean(this.closest('#MainContent .product-information__media > media-gallery'));
  }

  get #productZoomActive() {
    return this.closest('media-gallery')?.hasAttribute('data-photo-zoom-active') ||
      (window.visualViewport?.scale ?? 1) > 1.01;
  }

  get #isResourceCarousel() {
    return this.classList.contains('resource-list__carousel');
  }

  #handleResourceTouchPointerDown = (event) => {
    if (event.pointerType === 'touch') this.#handleMouseDown(event);
  };

  /**
   * Handles the 'mousedown' event to start dragging slides.
   * @param {MouseEvent} event - The mousedown event.
   */
  #handleMouseDown = (event) => {
    if (this.#isProductGallery) return;
    const { slides } = this;

    if (!slides || slides.length <= 1) return;
    if (!(event.target instanceof Element)) return;
    if (this.disabled || this.#disabled || this.#dragging || event.button !== 0) return;
    const cardGallery = !!this.closest('product-card');
    if ((this.#isResourceCarousel || cardGallery) && (
      event.ctrlKey || event.metaKey || event.shiftKey || event.altKey ||
      event.target.closest('button, input, select, textarea, label, [role="button"], [contenteditable="true"]')
    )) return;

    // A product card can contain its own image slideshow. Inside a resource
    // carousel the outer reel owns desktop dragging, so the same pointer does
    // not make the card image and the product row fight each other.
    const containingResourceSlide = this.closest('.resource-list__slide');
    const parentResourceCarousel = containingResourceSlide?.closest('slideshow-component.resource-list__carousel');
    if (!this.#isResourceCarousel && parentResourceCarousel && parentResourceCarousel !== this) return;

    // Check if the event target is within a 3D model interactive element
    // This prevents the slideshow from capturing drag events when interacting with 3D models
    if (event.target.closest('model-viewer')) {
      return;
    }

    // Preserve taps and vertical native page scrolling on touch. Horizontal
    // touch movement is owned by the reel through `touch-action: pan-y`.
    if (!this.#isResourceCarousel && !cardGallery && (!('pointerType' in event) || event.pointerType !== 'touch')) event.preventDefault();
    // Store initial position but don't start handling yet
    const { axis } = this.#scroll;
    const startPosition = event[axis];
    const crossAxis = axis === 'x' ? 'y' : 'x';
    const startCrossPosition = event[crossAxis];
    let activePointerId = 'pointerId' in event ? event.pointerId : undefined;

    const controller = new AbortController();
    this.#desktopDragController = controller;
    const generation = ++this.#dragGeneration;
    const { signal } = controller;
    const resourceCarousel = this.#isResourceCarousel;
    const flowingDrag = resourceCarousel;
    if (resourceCarousel) clearTimeout(this.#resourceResumeTimeout);
    if (this.autoplay) this.suspend();
    const scroller = this.refs.scroller;
    // Keep click/focus on press, but stop native image/link drag taking over.
    if (resourceCarousel || cardGallery) scroller.addEventListener('dragstart', preventDefault, { signal });
    if (flowingDrag) this.#scroll.snap = false;
    const scrollKey = axis === 'x' ? 'scrollLeft' : 'scrollTop';
    const initialScroll = scroller[scrollKey];
    const initialMaximumScroll = axis === 'x' ? scroller.scrollWidth - scroller.clientWidth : scroller.scrollHeight - scroller.clientHeight;
    const retainedResourceBridges = this.#resourceLoopBridges.filter((bridge) => bridge.isConnected);
    let resourceLoopDirection = retainedResourceBridges.length ? this.#resourceLoopDirection : 0;
    let resourceOriginalStart = retainedResourceBridges.length ? this.#resourceLoopOriginalStart : 0;
    let resourceOriginalEnd = retainedResourceBridges.length ? this.#resourceLoopOriginalEnd : initialMaximumScroll;
    let minimumScroll = 0;
    let maximumScroll = initialMaximumScroll;
    const edgeTolerance = Math.max(8, Math.min(48, scroller.clientWidth * 0.03));
    let preparedResourceBridges = retainedResourceBridges.length ? retainedResourceBridges : undefined;
    const retainedResourceSeam = Boolean(preparedResourceBridges?.length);
    let crossedResourceSeam = retainedResourceSeam && (
      (resourceLoopDirection > 0 && initialScroll > resourceOriginalEnd + 4) ||
      (resourceLoopDirection < 0 && initialScroll < resourceOriginalStart - 4)
    );
    let startScroll = scroller[scrollKey];
    const updateResourceDragBounds = () => {
      minimumScroll = 0;
      maximumScroll = axis === 'x' ? scroller.scrollWidth - scroller.clientWidth : scroller.scrollHeight - scroller.clientHeight;
      if (!preparedResourceBridges?.[0] || axis !== 'x') return;
      const bridgePosition = preparedResourceBridges[0].getBoundingClientRect().left -
        scroller.getBoundingClientRect().left + scroller.scrollLeft;
      if (resourceLoopDirection > 0) maximumScroll = Math.min(maximumScroll, bridgePosition);
    };
    const prepareResourceBridges = (direction) => {
      if (preparedResourceBridges || !resourceCarousel || !this.infinite) return;
      const beforeScroll = scroller[scrollKey];
      preparedResourceBridges = this.#stageResourceLoop(direction);
      resourceLoopDirection = this.#resourceLoopDirection;
      resourceOriginalStart = this.#resourceLoopOriginalStart;
      resourceOriginalEnd = this.#resourceLoopOriginalEnd;
      startScroll += scroller[scrollKey] - beforeScroll;
      updateResourceDragBounds();
    };
    updateResourceDragBounds();
    let dragFrame;
    let targetScroll = startScroll;
    const applyDrag = () => {
      dragFrame = undefined;
      scroller.scrollTo({ [axis === 'x' ? 'left' : 'top']: targetScroll, behavior: 'instant' });
    };
    signal.addEventListener('abort', () => cancelAnimationFrame(dragFrame), { once: true });
    if (flowingDrag) applyDrag();
    const startTime = performance.now();
    let lastMoveTime = startTime;
    let previous = startPosition;
    let velocity = 0;
    let moved = false;
    let distanceTravelled = 0;

    this.#dragging = true;

    /**
     * Handles the 'pointermove' event to update the scroll position.
     * @param {PointerEvent} event - The pointermove event.
     */
    const onPointerMove = (event) => {
      if (activePointerId !== undefined && event.pointerId !== activePointerId) return;
      activePointerId ??= event.pointerId;
      const current = event[axis];
      const initialDelta = startPosition - current;
      const crossDelta = startCrossPosition - event[crossAxis];
      const touchPointer = 'pointerType' in event && event.pointerType === 'touch';

      // Collection card photos are links first. Ordinary mouse jitter must
      // not capture the pointer and turn a quick product click into a drag.
      if (cardGallery && !moved && (Math.abs(initialDelta) < 8 ||
          Math.abs(initialDelta) <= Math.abs(crossDelta) * 1.15)) return;

      if (touchPointer && !moved) {
        if (Math.abs(initialDelta) < 5) return;
        if (Math.abs(initialDelta) <= Math.abs(crossDelta) * 1.15) return;
      }
      if (touchPointer && event.cancelable) event.preventDefault();

      if (resourceCarousel && initialDelta > 0 && resourceLoopDirection >= 0) {
        let projectedScroll = startScroll + initialDelta;
        const preloadDistance = Math.max(edgeTolerance, scroller.clientWidth * 2.5);
        if (projectedScroll >= resourceOriginalEnd - preloadDistance) {
          prepareResourceBridges(1);
          projectedScroll = startScroll + initialDelta;
        }
        if (projectedScroll > resourceOriginalEnd + 4) crossedResourceSeam = true;
      } else if (resourceCarousel && initialDelta < 0 && resourceLoopDirection <= 0) {
        let projectedScroll = startScroll + initialDelta;
        const preloadDistance = Math.max(edgeTolerance, scroller.clientWidth * 2.5);
        if (projectedScroll <= resourceOriginalStart + preloadDistance) {
          prepareResourceBridges(-1);
          projectedScroll = startScroll + initialDelta;
        }
        if (projectedScroll < resourceOriginalStart - 4) crossedResourceSeam = true;
      }

      if (!moved && Math.abs(initialDelta) < (resourceCarousel ? 8 : flowingDrag ? 5 : 1)) return;

      if (!moved) {
        moved = true;
        if (cardGallery) this.#scroll.snap = false;
        this.setPointerCapture(event.pointerId);

        // Prevent clicks once the user starts dragging
        if (!flowingDrag && !cardGallery) document.addEventListener('click', preventDefault, { once: true, signal });

        const movingRight = initialDelta < 0;
        const movingLeft = initialDelta > 0;

        // Check if the current slideshow should handle this drag
        const closestSlideshow = this.parentElement?.closest('slideshow-component');
        const isNested = closestSlideshow instanceof Slideshow && closestSlideshow !== this;
        const cannotMoveInDirection = (movingRight && this.atStart) || (movingLeft && this.atEnd);

        // Abort and let the parent slideshow handle the drag if we're moving in a direction where nested slideshow can't move
        if (isNested && cannotMoveInDirection) {
          controller.abort();
          return;
        }

        this.dispatchEvent(new CustomEvent(DRAG_INTENT_EVENT, {
          bubbles: true,
          composed: true,
          detail: { pointerId: event.pointerId },
        }));
        resourceCarousel ? this.suspend() : this.pause();
        this.setAttribute('dragging', '');
      }

      // Stop the event from bubbling up to parent slideshow components
      event.stopImmediatePropagation();

      const delta = previous - current;
      const now = performance.now();
      const timeDelta = Math.max(flowingDrag ? 8 : 1, now - (flowingDrag ? lastMoveTime : startTime));
      const sampleVelocity = (delta / timeDelta) * 1000;
      velocity = flowingDrag ? velocity * 0.7 + clamp(sampleVelocity, -3500, 3500) * 0.3 : Math.round(sampleVelocity);
      lastMoveTime = now;
      previous = current;
      distanceTravelled += Math.abs(delta);

      if (flowingDrag) {
        targetScroll = clamp(startScroll + initialDelta, minimumScroll, maximumScroll);
        if (dragFrame === undefined) dragFrame = requestAnimationFrame(applyDrag);
      } else {
        this.#scroll.by(delta, { instant: true });
      }
    };

    /**
     * Handles the 'pointerup' event to stop dragging slides.
     * @param {PointerEvent} event - The pointerup event.
     */
    const onPointerUp = async (event) => {
      if (activePointerId !== undefined && event.pointerId !== activePointerId) return;
      // Touch starts with implicit capture on the image/link. Transferring it
      // to this slideshow emits a bubbling loss event from that child; only
      // loss of our own capture ends the active gesture.
      if (event.type === 'lostpointercapture' && event.target !== this) return;
      if ((flowingDrag || cardGallery) && moved) {
        if (flowingDrag) {
          cancelAnimationFrame(dragFrame);
          applyDrag();
        }
        // The click follows pointerup, after the drag listeners are removed.
        if (event.type === 'pointerup') {
          const clearClickGuard = () => {
            this.removeEventListener('click', suppressClick, { capture: true });
            this.removeEventListener('pointerdown', clearClickGuard, { capture: true });
          };
          const suppressClick = (click) => {
            // Keyboard activation and a new intentional press are not drags.
            if (click.detail === 0) return;
            clearClickGuard();
            click.preventDefault();
            click.stopImmediatePropagation();
          };
          this.addEventListener('click', suppressClick, { capture: true });
          this.addEventListener('pointerdown', clearClickGuard, { capture: true, once: true });
          setTimeout(clearClickGuard, 250);
        }
      }
      controller.abort();
      const { current, slides } = this;
      const { scroller } = this.refs;

      this.#dragging = false;
      if (cardGallery && !moved) {
        // Do not move/sync the image between pointerup and the native click.
        this.#scroll.snap = true;
        this.resume();
        return;
      }
      if (flowingDrag) {
        this.removeAttribute('dragging');
        if (this.hasPointerCapture(event.pointerId)) this.releasePointerCapture(event.pointerId);
      }

      if (!slides?.length || !scroller) return;
      if (resourceCarousel && moved) this.#scheduleResourceCarouselResume(generation);

      if (flowingDrag && !moved) {
        if (preparedResourceBridges && !retainedResourceSeam) this.#clearResourceLoopBridges();
        scroller.style.setProperty('scroll-snap-type', 'none');
        this.resume();
        return;
      }

      const direction = Math.sign(velocity);
      const next = this.#sync();

      const releaseDelta = startPosition - event[axis];
      const resourceBridgeStart = preparedResourceBridges?.[0]
        ? preparedResourceBridges[0].getBoundingClientRect().left - scroller.getBoundingClientRect().left + scroller.scrollLeft
        : Infinity;
      const resourceBridgeReach = resourceLoopDirection < 0
        ? 0
        : Math.min(resourceBridgeStart, maximumScroll);
      const resourceBridgeAligned = resourceLoopDirection < 0
        ? scroller.scrollLeft <= resourceBridgeReach + 1
        : scroller.scrollLeft >= resourceBridgeReach - 1;
      const releaseFollowsResourceLoop = (resourceLoopDirection > 0 && releaseDelta > 0) ||
        (resourceLoopDirection < 0 && releaseDelta < 0);
      const wrapsResourceOnRelease = resourceCarousel && this.infinite && moved && distanceTravelled >= 20 &&
        event.type === 'pointerup' && crossedResourceSeam && releaseFollowsResourceLoop && resourceBridgeAligned;
      if (wrapsResourceOnRelease) {
        this.removeAttribute('dragging');
        if (this.hasPointerCapture(event.pointerId)) this.releasePointerCapture(event.pointerId);
        const targetIndex = resourceLoopDirection < 0
          ? Math.max(0, slides.length - (preparedResourceBridges?.length || 1))
          : 0;
        await this.#loopResourceCarousel(
          targetIndex,
          resourceLoopDirection < 0,
          event,
          preparedResourceBridges
        );
        return;
      }

      // Keep the extended track through release and settling. Removing it here
      // clamps scrollLeft to the original end before momentum and card-edge
      // selection run (often around card three in a six-card desktop reel).

      if (resourceCarousel) {
        // Project the flick, then settle on a physical card edge, including
        // loop copies. Unreachable edges must not become partial-card stops.
        const canCoast = moved && distanceTravelled >= 10 && event.type === 'pointerup' &&
          performance.now() - lastMoveTime < 140 && !prefersReducedMotion();
        const coast = canCoast ? clamp(velocity * 0.22, -scroller.clientWidth * 1.5, scroller.clientWidth * 1.5) : 0;
        const projectedDestination = clamp(scroller.scrollLeft + coast, minimumScroll, maximumScroll);
        const trackLeft = scroller.getBoundingClientRect().left;
        const cardEdges = [...scroller.querySelectorAll(':scope > .resource-list__slide')]
          .map((slide) => slide.getBoundingClientRect().left - trackLeft + scroller.scrollLeft)
          .filter((edge) => edge >= minimumScroll - 1 && edge <= maximumScroll + 1);
        const destination = cardEdges.length
          ? clamp(cardEdges.reduce((closest, edge) =>
              Math.abs(edge - projectedDestination) < Math.abs(closest - projectedDestination) ? edge : closest
            ), minimumScroll, maximumScroll)
          : projectedDestination;
        scroller.style.setProperty('scroll-snap-type', 'none');
        this.#scroll.to(destination, { instant: prefersReducedMotion() });
        this.removeAttribute('dragging');
        if (this.hasPointerCapture(event.pointerId)) this.releasePointerCapture(event.pointerId);
        await this.#scroll.finished;
        if (this.#dragging || generation !== this.#dragGeneration || !this.isConnected) return;
        if (preparedResourceBridges?.[0]) {
          const settledBridgeStart = preparedResourceBridges[0].getBoundingClientRect().left -
            scroller.getBoundingClientRect().left + scroller.scrollLeft;
          const settledBridgeReach = resourceLoopDirection < 0
            ? 0
            : Math.min(settledBridgeStart, scroller.scrollWidth - scroller.clientWidth);
          const reachedSettledBridge = resourceLoopDirection < 0
            ? scroller.scrollLeft <= settledBridgeReach + 1
            : scroller.scrollLeft >= settledBridgeReach - 1;
          if (crossedResourceSeam && reachedSettledBridge) {
            const targetIndex = resourceLoopDirection < 0
              ? Math.max(0, slides.length - preparedResourceBridges.length)
              : 0;
            await this.#loopResourceCarousel(
              targetIndex,
              resourceLoopDirection < 0,
              event,
              preparedResourceBridges
            );
            return;
          }
          const returnedToOriginals = resourceLoopDirection < 0
            ? scroller.scrollLeft >= resourceOriginalStart
            : scroller.scrollLeft <= resourceOriginalEnd;
          if (returnedToOriginals) {
            this.#clearResourceLoopBridges();
            preparedResourceBridges = undefined;
          }
        }
        const settledIndex = this.#sync();
        const settledSlide = slides[settledIndex];
        if (settledSlide) {
          this.dispatchEvent(new SlideshowSelectEvent({
            index: settledIndex, previousIndex: current, userInitiated: true, trigger: 'drag',
            slide: settledSlide, id: settledSlide.getAttribute('slide-id'),
          }));
        }
        return;
      }

      const modifier = current !== next || Math.abs(velocity) < 10 || distanceTravelled < 10 ? 0 : direction;
      let newIndex = clamp(next + modifier, 0, slides.length - 1);

      const newSlide = slides[newIndex];
      const currentIndex = this.current;

      if (!newSlide) throw new Error(`Slide not found at index ${newIndex}`);

      this.#scroll.to(newSlide);

      this.removeAttribute('dragging');
      if (this.hasPointerCapture(event.pointerId)) this.releasePointerCapture(event.pointerId);

      this.#centerSelectedThumbnail(newIndex);

      this.dispatchEvent(
        new SlideshowSelectEvent({
          index: newIndex,
          previousIndex: currentIndex,
          userInitiated: true,
          trigger: 'drag',
          slide: newSlide,
          id: newSlide.getAttribute('slide-id'),
        })
      );

      this.current = newIndex;

      await this.#scroll.finished;

      // It's possible that the user started dragging again before the scroll finished
      if (this.#dragging || generation !== this.#dragGeneration || !this.isConnected) return;

      this.#scroll.snap = true;
      this.resume();
    };

    if (!flowingDrag && !cardGallery) this.#scroll.snap = false;

    document.addEventListener('pointermove', onPointerMove, { signal });
    document.addEventListener('pointerup', onPointerUp, { signal });
    /**
     * pointerDown calls onPointerUp to fix an issue where the first tap-and-drag
     * on the zoom dialog is captured by the pointerMove/pointerUp listeners,
     * sometimes causing the slideshow to change slides unexpectedly
     */
    if (event.type !== 'pointerdown') document.addEventListener('pointerdown', onPointerUp, { signal });
    document.addEventListener('pointercancel', onPointerUp, { signal });
    document.addEventListener('lostpointercapture', onPointerUp, { signal });
  };

  #handlePointerEnter = () => {
    this.setAttribute('actioned', '');
  };

  get slides() {
    return this.refs.slides?.filter((slide) => !slide.hasAttribute('hidden') || slide.hasAttribute('reveal'));
  }

  /**
   * The initial slide index.
   * @type {number}
   */
  get initialSlideIndex() {
    const initialSlide = this.getAttribute('initial-slide');
    if (initialSlide == null) return 0;

    return parseInt(initialSlide, 10);
  }

  /**
   * Pause the slideshow when the page is hidden.
   */
  #handleVisibilityChange = () => (document.hidden ? this.suspend() : this.resume());

  #updateControlsVisibility() {
    if (!this.hasAttribute('auto-hide-controls')) return;

    const { scroller, slideshowControls } = this.refs;

    if (!(slideshowControls instanceof HTMLElement)) return;

    slideshowControls.hidden = scroller.scrollWidth <= scroller.offsetWidth;
  }

  /**
   * Setup IntersectionObserver for efficient visibility tracking of slides
   */
  #setupIntersectionObserver() {
    const { slides, scroller } = this.refs;
    if (!slides?.length) return;

    if (this.#intersectionObserver) {
      this.#intersectionObserver.disconnect();
    }

    this.#intersectionObserver = new IntersectionObserver(
      (entries) => {
        const allEntries = [
          ...entries,
          ...(this.#intersectionObserver ? this.#intersectionObserver.takeRecords() : []),
        ];

        for (const entry of allEntries) {
          const slide = /** @type {HTMLElement} */ (entry.target);
          const isCurrentlyVisible = this.#visibleSlides.includes(slide);
          const shouldBeVisible = entry.intersectionRatio >= SLIDE_VISIBLITY_THRESHOLD;

          if (shouldBeVisible && !isCurrentlyVisible) {
            this.#visibleSlides.push(slide);
          } else if (!shouldBeVisible && isCurrentlyVisible) {
            const index = this.#visibleSlides.indexOf(slide);
            if (index > -1) {
              this.#visibleSlides.splice(index, 1);
            }
          }
        }

        this.#visibleSlides.sort((a, b) => slides.indexOf(a) - slides.indexOf(b));
        this.#updateVisibleSlides();
      },
      {
        root: scroller,
        threshold: SLIDE_VISIBLITY_THRESHOLD,
        // Add small margin to account for sub-pixel rendering
        rootMargin: '1px',
      }
    );

    // Observe all slides - observer will fire initial callback asynchronously
    slides.forEach((slide) => {
      this.#intersectionObserver?.observe(slide);
    });
  }

  /**
   * Centers the selected thumbnail in the thumbnails container
   * @param {number} index - The index of the selected thumbnail
   * @param {ScrollBehavior} [behavior] - The scroll behavior.
   */
  #centerSelectedThumbnail(index, behavior = 'smooth') {
    const rails = this.querySelectorAll('product-thumbnail-rail');
    if (rails.length) {
      for (const rail of rails) {
        if (!rail.clientWidth || !rail.clientHeight) continue;
        const selected = rail.querySelector(`[data-thumbnail-index="${index}"]`);
        const viewport = rail.querySelector('.slideshow-controls__thumbnails-container');
        if (selected && viewport) {
          scrollIntoView(selected, { ancestor: viewport, behavior, block: 'center', inline: 'center' });
        }
      }
      return;
    }
    const selectedThumbnail = this.refs.thumbnails?.[index];
    if (!selectedThumbnail) return;

    const { thumbnailsContainer } = this.refs;
    if (!thumbnailsContainer || !(thumbnailsContainer instanceof HTMLElement)) return;

    const { slideshowControls } = this.refs;
    if (!slideshowControls || !(slideshowControls instanceof HTMLElement)) return;

    scrollIntoView(selectedThumbnail, {
      ancestor: thumbnailsContainer,
      behavior,
      block: 'center',
      inline: 'center',
    });
  }

  #updateVisibleSlides() {
    const { slides } = this;
    if (!slides || !slides.length) return 0;

    if (this.#isProductGallery) {
      slides.forEach((slide, index) => slide.setAttribute('aria-hidden', `${index !== this.current}`));
      return 1;
    }

    const visibleSlides = this.visibleSlides;

    // If the IntersectionObserver reports zero visible slides, then the slideshow has intersected, but no slides meet the visibility threshold.
    // Probably that the slideshow's host is mid-animation or zero-layout, like in an animating modal.
    // Don't stamp aria-hidden="true" on every slide. Set the current slide to the first visible slide.
    if (visibleSlides.length === 0) return 0;

    // Batch writes to the DOM
    scheduler.schedule(() => {
      // Update aria-hidden based on visibility
      slides.forEach((slide) => {
        const isVisible = visibleSlides.includes(slide);
        slide.setAttribute('aria-hidden', `${!isVisible}`);
      });
    });

    return visibleSlides.length;
  }
}

if (!customElements.get('slideshow-component')) {
  customElements.define('slideshow-component', Slideshow);
}
